/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import ManufacturerSpreaderDetails from 'manufacturer/ManufacturerSpreaderDetails';
import SpreaderAccordionMessageTime from 'manufacturer/SpreaderAccordionMessageTime';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {AssetDetailsSectionAccordion} from '@ion/dls/src/components/assetdetails/current/AssetDetailsSectionAccordion';
import {EmptyStateRow} from '@ion/dls/src/components/assetdetails/current/EmptyStateRow';
import {SkeletonLoader} from '@ion/dls/src/components/loading/SkeletonLoader';
import {useStyles} from './accordionStyles';

const SKELETON_MARGIN_BOTTOM = '8px';

// the Spreader section's loading skeleton - shared with the legacy
// accordion's skeleton in SpreaderMessageAccordion ...
export const SKELETON_HEIGHT = '16px';
export const SKELETON_COUNT  = 5;
export const skeletonStyles  = {marginBottom: SKELETON_MARGIN_BOTTOM};

export interface SpreaderCurrentFallbackProps {
  isLoading?: boolean;
  hasData: boolean;
  measures: GenericObject;
  sensorType: string;
  translations: GenericObject;
  messageTime?: string;
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

const useWithStyles = makeStyles(() => ({
  skeleton : {
    display       : 'flex',
    flexDirection : 'column'
  }
}));

// the Spreader section card for a manufacturer the current theme has no
// designed layout for - loading, without data, or with data it can only
// show through the legacy per-manufacturer content ...
export const SpreaderCurrentFallback = ({
  isLoading,
  hasData,
  measures,
  sensorType,
  translations,
  messageTime,
  expanded,
  onExpand
}: SpreaderCurrentFallbackProps) => {
  const styles  = useWithStyles();
  const classes = useStyles();
  const loaders = Array(SKELETON_COUNT).fill(null);

  let content: React.ReactElement = null;

  if (hasData) {
    content = (
      <div className={classes.assetCardListContentWrapper}>
        <ManufacturerSpreaderDetails measures={measures}
          sensorType={sensorType}
          translations={translations} />
        <SpreaderAccordionMessageTime messageTime={messageTime} />
      </div>
    );
  } else if (isLoading) {
    content = (
      <div className={styles.skeleton}>
        {loaders.map(function(_, index) {
          return (
            <SkeletonLoader key={index}
              height={SKELETON_HEIGHT}
              styles={skeletonStyles} />
          );
        })}
      </div>
    );
  } else {
    content = (
      <EmptyStateRow message={translate('tracking:spreader.noData')}
        tagLabel={translate('common:noData.noData')} />
    );
  }

  return (
    <AssetDetailsSectionAccordion title={translate('tracking:spreader.header')}
      expanded={expanded}
      onExpand={onExpand}>
      {content}
    </AssetDetailsSectionAccordion>
  );
};

export default React.memo(SpreaderCurrentFallback);
